import * as React from 'react';
import * as UI from 'core/ui';
import { Result, SessionProvider } from 'core/infrastructure';
import { Res } from 'core/api';
import { BackActionButton, Content, Footer, Header, PhoneOnly, PosScreen } from 'pos/ui';
import { Currency } from 'core/utils';
import { ExternalPaymentsService } from 'pos/services';
import { CashdrawerFacade, WarehouseFacade } from 'pos/facades';
import { PaymentDTOFactory } from 'pos/modules/helpers/payments-processing';
import { Message, Modal } from 'core/ui-next/modal';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { z } from 'zod';
import { BasicButton } from 'core/ui-next';
import { VStack, Text, Box } from '@chakra-ui/react';
import { zMonetary } from 'core/infrastructure-next';

type CashOperationType = 'INPUT' | 'OUTPUT';

type Props = {
	operationType: CashOperationType;
	existingSuppliers: string[];
} & Modal.Actions<void>;

const stateSchema = z.object({
	selectedSupplier: z.string().optional(),
	description: z.string().nonempty(Res.Msg.MSG_CASH_OPERATION_CONCEPT_CANNOT_BE_EMPTY()),
	amount: zMonetary({ strictPositive: true, required: true })
});

type State = z.infer<typeof stateSchema>;

function RegisterCashTransactionScreen(props: Props) {

	const { operationType, existingSuppliers, cancel, close } = props;

	const onCancel = React.useCallback(async () => cancel(), [cancel]);

	const { Form, Input, DecimalInput, ComboBox } = useControlsFor<State>();
	const formRef = React.useRef<FormRef<State>>(null);

	const initialValue: State = {
		selectedSupplier: '',
		description: '',
		amount: 0
	};

	const registerCashTransaction = async () => {
		const formValues = await formRef.current!.getValue();
		if (!formValues.ok) {
			return;
		}

		const printReceiptResult = await Message.confirm(Res.Msg.MSG_CONFIRM_PRINT_CASH_TRANSACTION());
		const printReceipt = printReceiptResult.ok ? true : false;

		const externalPayments = operationType === 'INPUT' ? ExternalPaymentsService.forPay('NoDocument', null) : ExternalPaymentsService.forRefund('NoDocument', null);
		const payment = PaymentDTOFactory.createCash(formValues.value.amount);

		const paymentsResult = await externalPayments.register(payment);
		if (!paymentsResult.ok) {
			cancel();
			return;
		}

		const cashControlResult = await externalPayments.commitCashPayments(0);
		if (!cashControlResult.ok) {
			cancel();
			return;
		}

		const selectedSupplier = formValues.value.selectedSupplier ?? '';

		const result = operationType === 'INPUT'
			? await CashdrawerFacade.registerInputOperation(selectedSupplier, formValues.value.description, formValues.value.amount, printReceipt, false)
			: await CashdrawerFacade.registerOutputOperation(selectedSupplier, formValues.value.description, formValues.value.amount, printReceipt, false);

		if (!result.Printed) {
			await Message.show(Res.Msg.MSG_REGISTER_CASH_TRANSACTION_PRINT_ERROR());
		}

		if (!result.Registered) {

			if (operationType === 'OUTPUT')
				await Message.show(Res.Msg.MSG_INVALID_TRANSACTION_BECAUSE_POS_AMOUNT_BECOMES_NEGATIVE(UI.Format.asMoney(formValues.value.amount)));

			await externalPayments.undoAll();
			cancel();
			return;
		}

		close();
	};

	return (
		<PosScreen screenId={'REGISTER_CASH_TRANSACTION'} size={'small'} useKeyPad={true}>
			<Header title={operationType === 'INPUT' ? Res.Web.REGISTER_INPUT_CASH_TRANSACTION() : Res.Web.REGISTER_OUTPUT_CASH_TRANSACTION()} onClose={cancel}></Header>
			<Content>
				<VStack width={'full'} height={'full'} padding={'6'} gap={'6'} overflow={'auto'}>
					<Text justifyContent={'center'} fontSize={'sm'} fontWeight={'semibold'}>{operationType === 'INPUT' ? Res.Web.REGISTER_INPUT_CASH_TRANSACTION_TITLE() : Res.Web.REGISTER_OUTPUT_CASH_TRANSACTION_TITLE()}</Text>
					<Box width={'full'}>
						<Form ref={formRef} initialValue={initialValue} schema={stateSchema}>
							<VStack width={'full'} gap={'4'}>
								<ComboBox label={Res.Web.REGISTER_CASH_OPERATION_PROVIDER_LBL()} linkTo={'selectedSupplier'} placeholder={Res.Web.REGISTER_CASH_OPERATION_PROVIDER_PLACEHOLDER()} options={existingSuppliers} />
								{/* <Select linkTo={'selectedSupplier'} label={Res.Web.REGISTER_CASH_OPERATION_PROVIDER_LBL()} placeholder={Res.Web.REGISTER_CASH_OPERATION_PROVIDER_PLACEHOLDER()} options={existingSuppliers} renderOption={opt => opt ?? ''} valueGetter={opt => opt ?? ''} /> */}
								<DecimalInput linkTo={'amount'} label={Res.Web.REGISTER_CASH_OPERATION_QUANTITY_LBL(Currency.SYMBOL)} precision={2} onKeyDown={UI.whenEnterKey(registerCashTransaction)} required />
								<Input linkTo={'description'} label={Res.Web.REGISTER_CASH_OPERATION_REASON_LBL()} placeholder={Res.Web.REGISTER_CASH_OPERATION_REASON_PLACEHOLDER()} onKeyDown={UI.whenEnterKey(registerCashTransaction)} required />
							</VStack>
						</Form>
					</Box>
					<BasicButton type='success' width={'full'} text={Res.Common.OK_BTN()} onClick={registerCashTransaction} />
				</VStack>
			</Content>
			<Footer>
				<PhoneOnly>
					<BackActionButton action={onCancel} />
				</PhoneOnly>
			</Footer>
		</PosScreen>
	);
}

export async function registerCashOperation(operationType: CashOperationType): Promise<Result> {

	if (!SessionProvider.getCurrent().AllowCashTransactions) {
		await Message.show(Res.Msg.MSG_CURRENT_POS_DOES_NOT_ALLOW_CASH_TRANSACTIONS());
		return Result.error();
	}

	const regularSuppliers = (await WarehouseFacade.getRegularSuppliers()).map(s => s.FiscalName);

	return await Modal.show(RegisterCashTransactionScreen, { operationType: operationType, existingSuppliers: regularSuppliers });
}
